import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddAssetForm from './components/AddAssetForm';
import HoldingsTable from './components/HoldingsTable';
import HoldingTransactionsTable from './components/HoldingTransactionsTable';
import BackfillDividendsButton from './components/BackfillDividendsButton';
import DividendsPaginatedTable from './components/DividendsPaginatedTable';
import PortfolioAllocationChart from './components/PortfolioAllocationChart';
import PortfolioHero, {
  DEFAULT_METRIC_IDS,
  getAvailableHeroMetrics,
} from './components/PortfolioHero';
import PortfolioChart from './components/PortfolioChart';
import { uiStyles } from '../../theme/uiStyles';
import { slugify } from '../../utils/slugify';
import { xirr } from '../../utils/xirr';
import CustomDateInput from '../../components/ui/CustomDateInput';
import CustomSelect from '../../components/ui/CustomSelect';
import { useNavActions } from '../../context/NavActionsContext';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../api/apiClient';

// Safe default asset colors — deliberately avoids:
//   purple/indigo (#8b7cff / #6366f1)  — used by Total line
//   orange/amber  (#f59e0b)             — used by Invested line
//   cyan          (#06b6d4)             — used by Benchmark line
//   green         (#22c55e)             — used by P/L positive / Holdings line
//   red           (#ef4444)             — used by P/L negative
const DEFAULT_COLORS = [
  '#f472b6', // pink
  '#c084fc', // violet
  '#60a5fa', // blue
  '#14b8a6', // teal
  '#fde047', // yellow
  '#fb7185', // rose
  '#818cf8', // periwinkle
  '#e879f9', // fuchsia
  '#a78bfa', // lavender
  '#2dd4bf', // turquoise
  '#f9a8d4', // blush
  '#93c5fd', // sky-light
];

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const HERO_METRICS_STORAGE_KEY = 'portfolioHeroVisibleMetricIds';

function PortfolioDashboard({ portfolioId, portfolioSlug = '', externalBenchmarkTicker = null }) {
  const isMobile = useIsMobile();
  const { register } = useNavActions();
  const navigate = useNavigate();
  const { user }  = useAuth();
  const isFree    = !user?.tier || user.tier === 'Free';
  const [portfolio, setPortfolio] = useState(null);

  const ASSET_COLORS_STORAGE_KEY       = portfolioId ? `assetColors-${portfolioId}`       : null;
  const CURRENCY_COLORS_STORAGE_KEY    = portfolioId ? `currencyColors-${portfolioId}`    : null;
  const ASSET_TYPE_COLORS_STORAGE_KEY  = portfolioId ? `assetTypeColors-${portfolioId}`   : null;
  const CHART_LINE_COLORS_STORAGE_KEY  = portfolioId ? `chartLineColors-${portfolioId}`   : null;

  const [assetColors, setAssetColors] = useState(() => {
    if (!ASSET_COLORS_STORAGE_KEY) return {};
    try { return JSON.parse(localStorage.getItem(ASSET_COLORS_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });

  const [currencyColors, setCurrencyColors] = useState(() => {
    if (!CURRENCY_COLORS_STORAGE_KEY) return {};
    try { return JSON.parse(localStorage.getItem(CURRENCY_COLORS_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });

  const [assetTypeColors, setAssetTypeColors] = useState(() => {
    if (!ASSET_TYPE_COLORS_STORAGE_KEY) return {};
    try { return JSON.parse(localStorage.getItem(ASSET_TYPE_COLORS_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });

  // chartLineColors is read-only here (written by PortfolioSettingsPage), so read directly
  const chartLineColors = useMemo(() => {
    if (!CHART_LINE_COLORS_STORAGE_KEY) return {};
    try { return JSON.parse(localStorage.getItem(CHART_LINE_COLORS_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  }, [CHART_LINE_COLORS_STORAGE_KEY]);

  useEffect(() => {
    if (!ASSET_COLORS_STORAGE_KEY) return;
    try { localStorage.setItem(ASSET_COLORS_STORAGE_KEY, JSON.stringify(assetColors)); }
    catch {}
  }, [assetColors, ASSET_COLORS_STORAGE_KEY]);

  useEffect(() => {
    if (!CURRENCY_COLORS_STORAGE_KEY) return;
    try { localStorage.setItem(CURRENCY_COLORS_STORAGE_KEY, JSON.stringify(currencyColors)); }
    catch {}
  }, [currencyColors, CURRENCY_COLORS_STORAGE_KEY]);

  useEffect(() => {
    if (!ASSET_TYPE_COLORS_STORAGE_KEY) return;
    try { localStorage.setItem(ASSET_TYPE_COLORS_STORAGE_KEY, JSON.stringify(assetTypeColors)); }
    catch {}
  }, [assetTypeColors, ASSET_TYPE_COLORS_STORAGE_KEY]);

  const handleTickerColorChange   = (ticker, color) => setAssetColors(prev   => ({ ...prev, [ticker]: color }));
  const handleCurrencyColorChange = (label, color)  => setCurrencyColors(prev => ({ ...prev, [label]:  color }));
  const handleAssetTypeColorChange = (label, color) => setAssetTypeColors(prev => ({ ...prev, [label]: color }));
  // Always derive slug from the loaded portfolio name so links are correct
  // even when the page was opened via a legacy numeric-ID URL
  const actualPortfolioSlug = useMemo(
    () => slugify(portfolio?.name || '') || portfolioSlug,
    [portfolio, portfolioSlug]
  );
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyRange, setHistoryRange] = useState('all');
  const [historyLoading, setHistoryLoading] = useState(true);
  const [benchmarkPoints, setBenchmarkPoints] = useState([]);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkError, setBenchmarkError] = useState(false);

  const [assetType, setAssetType] = useState('Stock');
  const [ticker, setTicker] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [providerId, setProviderId] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState(null);

  const [quantity, setQuantity] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [buyDate, setBuyDate] = useState('');

  const [depositAmount, setDepositAmount] = useState('');
  const [depositCurrency, setDepositCurrency] = useState('');
  const [depositDate, setDepositDate] = useState('');

  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalCurrency, setWithdrawalCurrency] = useState('');
  const [withdrawalDate, setWithdrawalDate] = useState('');

  const [dividendTicker, setDividendTicker] = useState('');
  const [dividendAmount, setDividendAmount] = useState('');
  const [dividendCurrency, setDividendCurrency] = useState('');
  const [dividendDate, setDividendDate] = useState('');

  const [interestAmount, setInterestAmount] = useState('');
  const [interestCurrency, setInterestCurrency] = useState('');
  const [interestDate, setInterestDate] = useState('');

  const [feeAmountValue, setFeeAmountValue] = useState('');
  const [feeCurrency, setFeeCurrency] = useState('');
  const [feeDate, setFeeDate] = useState('');

  const [taxAmount, setTaxAmount] = useState('');
  const [taxCurrency, setTaxCurrency] = useState('');
  const [taxDate, setTaxDate] = useState('');

  const [conversionFromCurrency, setConversionFromCurrency] = useState('');
  const [conversionToCurrency, setConversionToCurrency] = useState('');
  const [conversionFromAmount, setConversionFromAmount] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const [conversionToAmount, setConversionToAmount] = useState('');
  const [conversionDate, setConversionDate] = useState('');
  const [conversionRateLoading, setConversionRateLoading] = useState(false);
  const [lastEditedConversionField, setLastEditedConversionField] = useState(null);

  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [tickerSuggestionsLoading, setTickerSuggestionsLoading] = useState(false);
  const [nameSuggestionsLoading, setNameSuggestionsLoading] = useState(false);

  const [showMetricSettings, setShowMetricSettings] = useState(false);
  const [visibleHeroMetricIds, setVisibleHeroMetricIds] = useState(() => {
    try {
      const stored = localStorage.getItem(HERO_METRICS_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : null;

      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch {
      // ignore localStorage parse errors
    }

    return DEFAULT_METRIC_IDS;
  });

  const tickerSearchTimeoutRef = useRef(null);
  const nameSearchTimeoutRef = useRef(null);
  const successTimeoutRef = useRef(null);

  // Register "Visible metrics" action in hamburger menu
  useEffect(() => {
    const unregister = register('dashboard-metrics', 'Visible metrics', () => setShowMetricSettings(v => !v));
    return unregister;
  }, [register]);

  useEffect(() => {
    try {
      localStorage.setItem(
        HERO_METRICS_STORAGE_KEY,
        JSON.stringify(visibleHeroMetricIds)
      );
    } catch {
      // ignore localStorage write errors
    }
  }, [visibleHeroMetricIds]);

  const toggleHeroMetric = (metricId) => {
    setVisibleHeroMetricIds((prev) => {
      const exists = prev.includes(metricId);

      if (exists) {
        if (prev.length === 1) {
          return prev;
        }

        return prev.filter((id) => id !== metricId);
      }

      return [...prev, metricId];
    });
  };

  const resetHeroMetrics = () => {
    setVisibleHeroMetricIds(DEFAULT_METRIC_IDS);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }

    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage('');
    }, 3500);
  };

  useEffect(() => {
    return () => {
      if (tickerSearchTimeoutRef.current) clearTimeout(tickerSearchTimeoutRef.current);
      if (nameSearchTimeoutRef.current) clearTimeout(nameSearchTimeoutRef.current);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const fetchPortfolio = async () => {
    if (!portfolioId) return;

    try {
      const response = await apiFetch('/api/portfolios/' + portfolioId);

      if (!response.ok) {
        throw new Error('Failed to load portfolio.');
      }

      const data = await response.json();
      setPortfolio(data);
    } catch (err) {
      setError(err.message || 'Failed to load portfolio.');
    }
  };

  const fetchSummary = async () => {
    if (!portfolioId) return;

    try {
      setSummaryLoading(true);
      setLoading(true);
      setError('');

      const response = await apiFetch(
        '/api/portfolio-v2/summary?portfolioId=' + portfolioId
      );

      if (!response.ok) {
        throw new Error('Failed to load portfolio summary.');
      }

      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to load portfolio summary.');
    } finally {
      setSummaryLoading(false);
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    if (!portfolioId) return;

    try {
      setTransactionsLoading(true);

      const response = await apiFetch(
        '/api/PortfolioTransactions?portfolioId=' + portfolioId
      );

      if (!response.ok) {
        throw new Error('Failed to load portfolio transactions.');
      }

      const data = await response.json();
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load portfolio transactions.');
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchHistory = async (rangeToLoad = historyRange) => {
    if (!portfolioId) return;

    try {
      setHistoryLoading(true);

      const response = await apiFetch(
        '/api/portfolio-v2/history?portfolioId=' +
          portfolioId +
          '&range=' +
          encodeURIComponent(rangeToLoad)
      );

      if (!response.ok) {
        throw new Error('Failed to load portfolio history.');
      }

      const data = await response.json();
      setHistoryPoints(Array.isArray(data?.points) ? data.points : []);
    } catch (err) {
      setError(err.message || 'Failed to load portfolio history.');
      setHistoryPoints([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Benchmark data is always fetched for the full history ('all') so that
  // lookupPriceNearest can find prices for every historical buy transaction date,
  // regardless of which chart range is currently selected. Fetching only the
  // selected range (e.g. 7D) would leave older buy dates without prices,
  // causing cumulativeBenchmarkUnits to stay near 0 and SPY % to show ~-100%.
  const fetchBenchmark = async (ticker = null, range = null) => {
    // Empty string means "no benchmark configured" — clear data and return.
    if (ticker === '') {
      setBenchmarkPoints([]);
      setBenchmarkError(false);
      return;
    }

    const benchmarkTicker = ticker || portfolio?.benchmarkTicker || '';
    if (!benchmarkTicker) {
      setBenchmarkPoints([]);
      setBenchmarkError(false);
      return;
    }

    // Use the provided range, or fall back to the current historyRange, or 'all'.
    const fetchRange = range || historyRange || 'all';

    try {
      setBenchmarkLoading(true);
      setBenchmarkError(false);
      const response = await apiFetch(
        `/api/stock/history/${encodeURIComponent(benchmarkTicker)}?range=${encodeURIComponent(fetchRange)}`
      );
      if (!response.ok) {
        setBenchmarkPoints([]);
        setBenchmarkError(true);
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        setBenchmarkPoints([]);
        setBenchmarkError(true);
        return;
      }
      setBenchmarkPoints(data);
      setBenchmarkError(false);
    } catch {
      setBenchmarkPoints([]);
      setBenchmarkError(true);
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const refreshPortfolioData = async () => {
    // Use the externally-set ticker first (from settings save), since
    // portfolio data in this component is not re-fetched after settings save.
    const activeBenchmarkTicker =
      externalBenchmarkTicker !== null
        ? externalBenchmarkTicker
        : portfolio?.benchmarkTicker || null;
    await Promise.all([
      fetchSummary(),
      fetchTransactions(),
      fetchHistory(historyRange),
      fetchBenchmark(activeBenchmarkTicker, historyRange),
    ]);
  };

  useEffect(() => {
    fetchPortfolio();
    fetchSummary();
    fetchTransactions();
    fetchHistory(historyRange);
    // Benchmark is NOT fetched here — portfolio hasn't loaded yet so
    // portfolio?.benchmarkTicker is undefined. We fetch it once portfolio loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  useEffect(() => {
    fetchHistory(historyRange);
    // Re-fetch benchmark with the same range so granularity matches (e.g. 30-min for 1D/7D).
    const activeTicker =
      externalBenchmarkTicker !== null
        ? externalBenchmarkTicker
        : portfolio?.benchmarkTicker || '';
    if (activeTicker) {
      fetchBenchmark(activeTicker, historyRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyRange]);

  // Re-fetch benchmark whenever the configured ticker changes (including initial load).
  useEffect(() => {
    if (portfolio?.benchmarkTicker) {
      fetchBenchmark(portfolio.benchmarkTicker);
    } else {
      // No benchmark configured on the portfolio — clear any stale data.
      // (If externalBenchmarkTicker has a value, its own useEffect handles the fetch.)
      setBenchmarkPoints([]);
      setBenchmarkError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio?.benchmarkTicker]);

  useEffect(() => {
    if (externalBenchmarkTicker !== null) {
      // Pass the ticker as-is:
      //   '' → fetchBenchmark will clear benchmarkPoints (no benchmark)
      //   'ASML' → fetchBenchmark will fetch ASML data
      fetchBenchmark(externalBenchmarkTicker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalBenchmarkTicker]);

  const effectiveBenchmarkTicker =
    externalBenchmarkTicker !== null
      ? externalBenchmarkTicker
      : portfolio?.benchmarkTicker || '';

  const reportCurrency = (summary?.baseCurrency || portfolio?.baseCurrency || 'USD').toUpperCase();

  useEffect(() => {
    if (!depositCurrency) setDepositCurrency(reportCurrency);
    if (!withdrawalCurrency) setWithdrawalCurrency(reportCurrency);
    if (!interestCurrency) setInterestCurrency(reportCurrency);
    if (!feeCurrency) setFeeCurrency(reportCurrency);
    if (!taxCurrency) setTaxCurrency(reportCurrency);
  }, [
    reportCurrency,
    depositCurrency,
    withdrawalCurrency,
    interestCurrency,
    feeCurrency,
    taxCurrency,
  ]);

  const cashBalances = useMemo(() => {
    return Array.isArray(summary?.cashBalances) ? summary.cashBalances : [];
  }, [summary]);

  const visibleCashBalances = useMemo(() => {
    return cashBalances.filter((item) => Number(item.amount || 0) !== 0);
  }, [cashBalances]);

  const hasNegativeTrackedCash = useMemo(() => {
    return cashBalances.some((item) => Number(item.amount || 0) < 0);
  }, [cashBalances]);

  const cashByCurrencyMap = useMemo(() => {
    const map = new Map();

    cashBalances.forEach((item) => {
      const currency = String(item?.currency || '').toUpperCase();
      if (!currency) return;
      map.set(currency, Number(item?.amount || 0));
    });

    return map;
  }, [cashBalances]);

  const getAvailableCash = (currency) => {
    const normalized = String(currency || '').toUpperCase();
    return Number(cashByCurrencyMap.get(normalized) || 0);
  };

  const availableCurrencies = useMemo(() => {
    const set = new Set([
      reportCurrency,
      'USD',
      'EUR',
      'CZK',
      'PLN',
      'GBP',
      'CHF',
      'JPY',
      'CAD',
      'AUD',
      'SEK',
      'NOK',
      'DKK',
    ]);

    cashBalances.forEach((item) => {
      if (item?.currency) {
        set.add(String(item.currency).toUpperCase());
      }
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cashBalances, reportCurrency]);

  const convertibleFromCurrencies = useMemo(() => {
    const currencies = cashBalances
      .filter((item) => Number(item.amount || 0) > 0)
      .map((item) => String(item.currency || '').toUpperCase())
      .filter(Boolean);

    if (currencies.length > 0) {
      return Array.from(new Set(currencies)).sort((a, b) => a.localeCompare(b));
    }

    return availableCurrencies;
  }, [cashBalances, availableCurrencies]);

  const convertibleToCurrencies = useMemo(() => {
    return availableCurrencies.filter((currency) => currency !== conversionFromCurrency);
  }, [availableCurrencies, conversionFromCurrency]);

  const holdingOptions = useMemo(() => {
    const safeHoldings = Array.isArray(summary?.holdings) ? summary.holdings : [];

    return safeHoldings
      .map((holding) => ({
        ticker: String(holding?.ticker || '').toUpperCase(),
        displayName: holding?.displayName || holding?.ticker || '',
        currency: String(holding?.currency || reportCurrency).toUpperCase(),
        instrumentId: holding?.instrumentId || null,
        openQuantity: Number(holding?.openQuantity || 0),
      }))
      .filter((item) => item.ticker);
  }, [summary, reportCurrency]);

  useEffect(() => {
    if (!dividendTicker && holdingOptions.length > 0) {
      setDividendTicker(holdingOptions[0].ticker);
    }
  }, [dividendTicker, holdingOptions]);

  const selectedDividendHolding = useMemo(() => {
    return holdingOptions.find((item) => item.ticker === dividendTicker) || null;
  }, [holdingOptions, dividendTicker]);

  useEffect(() => {
    if (selectedDividendHolding?.currency) {
      setDividendCurrency(selectedDividendHolding.currency);
    }
  }, [selectedDividendHolding]);

  useEffect(() => {
    if (!conversionFromCurrency) {
      if (convertibleFromCurrencies.length > 0) {
        setConversionFromCurrency(convertibleFromCurrencies[0]);
      } else {
        setConversionFromCurrency(reportCurrency);
      }
    }
  }, [reportCurrency, convertibleFromCurrencies, conversionFromCurrency]);

  useEffect(() => {
    if (!conversionToCurrency) {
      const fallbackTarget =
        availableCurrencies.find((currency) => currency !== conversionFromCurrency) ||
        reportCurrency;
      setConversionToCurrency(fallbackTarget);
      return;
    }

    if (conversionToCurrency === conversionFromCurrency) {
      const fallbackTarget =
        availableCurrencies.find((currency) => currency !== conversionFromCurrency) ||
        reportCurrency;
      setConversionToCurrency(fallbackTarget);
    }
  }, [reportCurrency, availableCurrencies, conversionFromCurrency, conversionToCurrency]);

  const loadConversionRate = async (fromCurrency, toCurrency, date) => {
    if (!fromCurrency || !toCurrency || !date || fromCurrency === toCurrency) {
      if (fromCurrency && toCurrency && fromCurrency === toCurrency) {
        setConversionRate('1');
      }
      return;
    }

    try {
      setConversionRateLoading(true);

      const response = await apiFetch(
        '/api/fx/rate?from=' +
          encodeURIComponent(fromCurrency) +
          '&to=' +
          encodeURIComponent(toCurrency) +
          '&date=' +
          encodeURIComponent(date)
      );

      if (!response.ok) {
        throw new Error('Failed to load FX rate.');
      }

      const data = await response.json();
      const rate = Number(data?.rate || 0);

      if (rate > 0) {
        setConversionRate(String(Number(rate.toFixed(8))));
      }
    } catch {
      // keep manual value
    } finally {
      setConversionRateLoading(false);
    }
  };

  useEffect(() => {
    if (
      conversionFromCurrency &&
      conversionToCurrency &&
      conversionDate &&
      lastEditedConversionField !== 'rate'
    ) {
      loadConversionRate(conversionFromCurrency, conversionToCurrency, conversionDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversionFromCurrency,
    conversionToCurrency,
    conversionDate,
    lastEditedConversionField,
  ]);

  useEffect(() => {
    const fromAmount = Number(conversionFromAmount);
    const rate = Number(conversionRate);
    const toAmount = Number(conversionToAmount);

    const hasFrom = !!conversionFromAmount && !Number.isNaN(fromAmount) && fromAmount > 0;
    const hasRate = !!conversionRate && !Number.isNaN(rate) && rate > 0;
    const hasTo = !!conversionToAmount && !Number.isNaN(toAmount) && toAmount > 0;

    if (lastEditedConversionField === 'from') {
      if (hasFrom && hasRate) {
        const calculatedTo = fromAmount * rate;
        setConversionToAmount(String(Number(calculatedTo.toFixed(8))));
      } else if (hasFrom && hasTo) {
        const calculatedRate = toAmount / fromAmount;
        setConversionRate(String(Number(calculatedRate.toFixed(8))));
      }
      return;
    }

    if (lastEditedConversionField === 'rate') {
      if (hasFrom && hasRate) {
        const calculatedTo = fromAmount * rate;
        setConversionToAmount(String(Number(calculatedTo.toFixed(8))));
      } else if (hasRate && hasTo) {
        const calculatedFrom = toAmount / rate;
        setConversionFromAmount(String(Number(calculatedFrom.toFixed(8))));
      }
      return;
    }

    if (lastEditedConversionField === 'to') {
      if (hasFrom && hasTo) {
        const calculatedRate = toAmount / fromAmount;
        setConversionRate(String(Number(calculatedRate.toFixed(8))));
      } else if (hasRate && hasTo) {
        const calculatedFrom = toAmount / rate;
        setConversionFromAmount(String(Number(calculatedFrom.toFixed(8))));
      }
    }
  }, [
    conversionFromAmount,
    conversionRate,
    conversionToAmount,
    lastEditedConversionField,
  ]);

  const sortTickerResults = (items, query) => {
    const q = query.trim().toUpperCase();

    return [...items].sort((a, b) => {
      const aSymbol = (a.symbol || '').toUpperCase();
      const bSymbol = (b.symbol || '').toUpperCase();

      const aStarts = aSymbol.startsWith(q) ? 0 : 1;
      const bStarts = bSymbol.startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;

      const aExact = aSymbol === q ? 0 : 1;
      const bExact = bSymbol === q ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;

      return aSymbol.localeCompare(bSymbol);
    });
  };

  const sortNameResults = (items, query) => {
    const q = query.trim().toLowerCase();

    return [...items].sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();

      const aStarts = aName.startsWith(q) ? 0 : 1;
      const bStarts = bName.startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;

      const aIncludes = aName.includes(q) ? 0 : 1;
      const bIncludes = bName.includes(q) ? 0 : 1;
      if (aIncludes !== bIncludes) return aIncludes - bIncludes;

      return aName.localeCompare(bName);
    });
  };

  const normalizeSuggestionItem = (item) => {
    if (!item) return null;

    const detectedCurrency =
      item.currency ||
      item.quoteCurrency ||
      item.financialCurrency ||
      item.marketCurrency ||
      '';

    return {
      ...item,
      symbol: item.symbol || '',
      name: item.name || item.shortname || item.symbol || '',
      currency: String(detectedCurrency || '').toUpperCase(),
      exchange: item.exchange || item.fullExchangeName || '',
      providerId: item.symbol || '',
    };
  };

  const searchTickerSuggestions = async (value) => {
    const query = value.trim();

    if (query.length < 1) {
      setTickerSuggestions([]);
      setTickerSuggestionsLoading(false);
      return;
    }

    try {
      setTickerSuggestionsLoading(true);

      const response = await apiFetch(
        '/api/stock/search?query=' + encodeURIComponent(query)
      );

      if (!response.ok) {
        throw new Error('Failed to load ticker suggestions.');
      }

      const data = await response.json();
      const safeData = Array.isArray(data) ? data.map(normalizeSuggestionItem) : [];
      setTickerSuggestions(sortTickerResults(safeData, query));
    } catch {
      setTickerSuggestions([]);
    } finally {
      setTickerSuggestionsLoading(false);
    }
  };

  const searchNameSuggestions = async (value) => {
    const query = value.trim();

    if (query.length < 1) {
      setNameSuggestions([]);
      setNameSuggestionsLoading(false);
      return;
    }

    try {
      setNameSuggestionsLoading(true);

      const response = await apiFetch(
        '/api/stock/search?query=' + encodeURIComponent(query)
      );

      if (!response.ok) {
        throw new Error('Failed to load name suggestions.');
      }

      const data = await response.json();
      const safeData = Array.isArray(data) ? data.map(normalizeSuggestionItem) : [];
      setNameSuggestions(sortNameResults(safeData, query));
    } catch {
      setNameSuggestions([]);
    } finally {
      setNameSuggestionsLoading(false);
    }
  };

  const handleTickerChange = (value) => {
    setTicker(value);
    setSelectedInstrument(null);

    if (tickerSearchTimeoutRef.current) {
      clearTimeout(tickerSearchTimeoutRef.current);
    }

    tickerSearchTimeoutRef.current = setTimeout(() => {
      searchTickerSuggestions(value);
    }, 200);
  };

  const handleNameChange = (value) => {
    setDisplayName(value);
    setSelectedInstrument(null);

    if (nameSearchTimeoutRef.current) {
      clearTimeout(nameSearchTimeoutRef.current);
    }

    nameSearchTimeoutRef.current = setTimeout(() => {
      searchNameSuggestions(value);
    }, 200);
  };

  const handleSelectSuggestion = (item) => {
    const normalizedItem = normalizeSuggestionItem(item);

    setTicker(normalizedItem?.symbol || '');
    setDisplayName(normalizedItem?.name || normalizedItem?.symbol || '');
    setProviderId(normalizedItem?.providerId || normalizedItem?.symbol || '');
    setSelectedInstrument(normalizedItem);
    setTickerSuggestions([]);
    setNameSuggestions([]);
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();

    const normalizedTicker = ticker.trim().toUpperCase();
    const parsedQuantity = Number(quantity);
    const parsedBuyPrice = Number(buyPrice);

    if (!normalizedTicker) {
      setError('Please enter a ticker.');
      return;
    }

    if (!parsedQuantity || parsedQuantity <= 0) {
      setError('Please enter a valid quantity greater than 0.');
      return;
    }

    if (!parsedBuyPrice || parsedBuyPrice <= 0) {
      setError('Please enter a valid buy price greater than 0.');
      return;
    }

    if (!buyDate) {
      setError('Please enter a buy date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const portfolioBaseCurrency =
        (portfolio?.baseCurrency || summary?.baseCurrency || 'USD').toUpperCase();

      const instrumentCurrency = (
        selectedInstrument?.currency ||
        selectedInstrument?.quoteCurrency ||
        'USD'
      ).toUpperCase();

      const fxRateToBase = instrumentCurrency === portfolioBaseCurrency ? 1 : 0;

      const payload = {
        portfolioId: Number(portfolioId),
        ticker: normalizedTicker,
        displayName: (displayName || normalizedTicker).trim(),
        assetType: mapAssetType(assetType),
        exchange: selectedInstrument?.exchange || null,
        providerId: providerId || selectedInstrument?.providerId || normalizedTicker,
        quoteCurrency: instrumentCurrency,
        transactionType: 0,
        quantity: parsedQuantity,
        pricePerUnit: parsedBuyPrice,
        tradeCurrency: instrumentCurrency,
        fxRateToBase,
        feeAmount: 0,
        occurredAt: new Date(buyDate + 'T12:00:00').toISOString(),
        notes: 'Buy from portfolio dashboard',
      };

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add asset. ' + errorText);
      }

      // Persist user-selected asset type so the allocation chart stays correct
      // even when the backend overrides assetType from the data provider.
      try {
        localStorage.setItem(`assetType:${portfolioId}:${normalizedTicker}`, assetType);
      } catch (_) {}

      setAssetType('Stock');
      setTicker('');
      setDisplayName('');
      setProviderId('');
      setSelectedInstrument(null);
      setQuantity('');
      setBuyPrice('');
      setBuyDate('');
      setTickerSuggestions([]);
      setNameSuggestions([]);

      await refreshPortfolioData();
      showSuccess('Asset was added successfully.');
    } catch (err) {
      setError(err.message || 'Failed to add asset.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(depositAmount);
    const selectedDepositCurrency = (depositCurrency || reportCurrency).toUpperCase();
    const fxRateToBase = selectedDepositCurrency === reportCurrency ? 1 : 0;

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid deposit amount.');
      return;
    }

    if (!depositDate) {
      setError('Please enter a deposit date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 3,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency: selectedDepositCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(depositDate + 'T12:00:00').toISOString(),
          notes: 'Deposit from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add deposit. ' + errorText);
      }

      setDepositAmount('');
      setDepositCurrency(reportCurrency);
      setDepositDate('');

      await refreshPortfolioData();
      showSuccess('Deposit was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create deposit.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(withdrawalAmount);
    const selectedCurrency = (withdrawalCurrency || reportCurrency).toUpperCase();
    const fxRateToBase = selectedCurrency === reportCurrency ? 1 : 0;

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid withdrawal amount.');
      return;
    }

    if (!withdrawalDate) {
      setError('Please enter a withdrawal date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 4,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency: selectedCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(withdrawalDate + 'T12:00:00').toISOString(),
          notes: 'Withdrawal from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add withdrawal. ' + errorText);
      }

      setWithdrawalAmount('');
      setWithdrawalCurrency(reportCurrency);
      setWithdrawalDate('');

      await refreshPortfolioData();
      showSuccess('Withdrawal was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create withdrawal.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDividend = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(dividendAmount);

    if (!selectedDividendHolding?.ticker) {
      setError('Please select a holding for dividend.');
      return;
    }

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid dividend amount.');
      return;
    }

    if (!dividendDate) {
      setError('Please enter a dividend date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const tradeCurrency = (
        dividendCurrency ||
        selectedDividendHolding.currency ||
        reportCurrency
      ).toUpperCase();

      const fxRateToBase = tradeCurrency === reportCurrency ? 1 : 0;

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          instrumentId: selectedDividendHolding.instrumentId,
          ticker: selectedDividendHolding.ticker,
          displayName: selectedDividendHolding.displayName,
          transactionType: 2,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency,
          quoteCurrency: tradeCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(dividendDate + 'T12:00:00').toISOString(),
          notes: 'Dividend from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add dividend. ' + errorText);
      }

      setDividendAmount('');
      setDividendDate('');

      await refreshPortfolioData();
      showSuccess('Dividend was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create dividend.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInterest = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(interestAmount);
    const selectedCurrency = (interestCurrency || reportCurrency).toUpperCase();
    const fxRateToBase = selectedCurrency === reportCurrency ? 1 : 0;

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid interest amount.');
      return;
    }

    if (!interestDate) {
      setError('Please enter an interest date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 6,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency: selectedCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(interestDate + 'T12:00:00').toISOString(),
          notes: 'Interest from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add interest. ' + errorText);
      }

      setInterestAmount('');
      setInterestDate('');

      await refreshPortfolioData();
      showSuccess('Interest was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create interest.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFee = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(feeAmountValue);
    const selectedCurrency = (feeCurrency || reportCurrency).toUpperCase();
    const fxRateToBase = selectedCurrency === reportCurrency ? 1 : 0;

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid fee amount.');
      return;
    }

    if (!feeDate) {
      setError('Please enter a fee date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 5,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency: selectedCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(feeDate + 'T12:00:00').toISOString(),
          notes: 'Fee from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add fee. ' + errorText);
      }

      setFeeAmountValue('');
      setFeeDate('');

      await refreshPortfolioData();
      showSuccess('Fee was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create fee.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTax = async (e) => {
    e.preventDefault();

    const parsedAmount = Number(taxAmount);
    const selectedCurrency = (taxCurrency || reportCurrency).toUpperCase();
    const fxRateToBase = selectedCurrency === reportCurrency ? 1 : 0;

    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid tax amount.');
      return;
    }

    if (!taxDate) {
      setError('Please enter a tax date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 7,
          quantity: parsedAmount,
          pricePerUnit: 1,
          tradeCurrency: selectedCurrency,
          fxRateToBase,
          feeAmount: 0,
          occurredAt: new Date(taxDate + 'T12:00:00').toISOString(),
          notes: 'Tax from portfolio dashboard',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add tax. ' + errorText);
      }

      setTaxAmount('');
      setTaxDate('');

      await refreshPortfolioData();
      showSuccess('Tax was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to create tax.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFxConversion = async (e) => {
    e.preventDefault();

    const fromCurrency = (conversionFromCurrency || '').toUpperCase();
    const toCurrency = (conversionToCurrency || '').toUpperCase();
    const fromAmount = Number(conversionFromAmount);
    const rate = Number(conversionRate);
    const toAmount = Number(conversionToAmount);

    if (!fromCurrency) {
      setError('Please select source currency.');
      return;
    }

    if (!toCurrency) {
      setError('Please select target currency.');
      return;
    }

    if (fromCurrency === toCurrency) {
      setError('Source and target currency must be different.');
      return;
    }

    if (!fromAmount || fromAmount <= 0) {
      setError('Please enter a valid source amount.');
      return;
    }

    if (!rate || rate <= 0) {
      setError('Please enter a valid FX rate.');
      return;
    }

    if (!toAmount || toAmount <= 0) {
      setError('Please enter a valid target amount.');
      return;
    }

    if (!conversionDate) {
      setError('Please enter a conversion date.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/api/PortfolioTransactions', {
        method: 'POST',
        body: JSON.stringify({
          portfolioId: Number(portfolioId),
          transactionType: 8,
          quantity: fromAmount,
          pricePerUnit: 1,
          tradeCurrency: fromCurrency,
          fxRateToBase: fromCurrency === reportCurrency ? 1 : 0,
          feeAmount: 0,
          counterCurrency: toCurrency,
          counterAmount: toAmount,
          occurredAt: new Date(conversionDate + 'T12:00:00').toISOString(),
          notes: `FX conversion ${fromCurrency} -> ${toCurrency}`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to convert cash. ' + errorText);
      }

      setConversionFromAmount('');
      setConversionRate('');
      setConversionToAmount('');
      setConversionDate('');
      setLastEditedConversionField(null);

      await refreshPortfolioData();
      showSuccess('FX conversion was saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to convert cash.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTransaction = async (transactionId, payload) => {
    setError('');
    setSuccessMessage('');

    const response = await apiFetch(
      '/api/PortfolioTransactions/' + transactionId,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error('Failed to update transaction. ' + errorText);
    }

    await refreshPortfolioData();
    showSuccess('Transaction was updated successfully.');
  };

  const handleDeleteTransaction = async (transactionId) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this transaction? This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch(
        '/api/PortfolioTransactions/' + transactionId,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to delete transaction. ' + errorText);
      }

      await refreshPortfolioData();
      showSuccess('Transaction was deleted successfully.');
    } catch (err) {
      setError(err.message || 'Failed to delete transaction.');
    } finally {
      setSubmitting(false);
    }
  };

  const walletOn = portfolio?.walletEnabled !== false;

  const buySellTransactions = useMemo(() => {
    return transactions.filter(
      (tx) => tx.transactionType === 0 || tx.transactionType === 1
    );
  }, [transactions]);

  const displayHistoryPoints = useMemo(() => {
    if (walletOn) return historyPoints;

    // Wallet OFF → "Invested" line represents cumulative cost basis of open
    // positions (in base currency). The backend now computes this with the
    // correct historical FX series and ships it as `investedCostBase` on each
    // point — read it directly rather than recomputing in the frontend (the
    // old client-side rollingCost used the stale `tx.fxRateToBase` which is
    // invalid if the portfolio base currency was later changed).
    return historyPoints.map((p) => {
      const investedCost = Number(
        p?.investedCostBase ?? p?.InvestedCostBase ?? 0
      );
      return {
        ...p,
        totalValueBase: p?.holdingsValueBase ?? p?.HoldingsValueBase ?? 0,
        cashValueBase: 0,
        CashValueBase: 0,
        netContributionsBase: Math.max(0, investedCost),
        NetContributionsBase: Math.max(0, investedCost),
      };
    });
  }, [historyPoints, walletOn]);

  const DUST = 1e-6;
  const groupedHoldings = useMemo(() => {
    const all = Array.isArray(summary?.holdings) ? summary.holdings : [];
    const filtered = all.filter((h) => Number(h?.openQuantity || 0) >= DUST);

    // Build ticker → assetType from transactions as a secondary source
    const txAssetTypeMap = {};
    transactions.forEach((tx) => {
      const ticker = String(tx.instrument?.ticker || tx.ticker || '').toUpperCase();
      const type = tx.instrument?.assetType ?? tx.assetType;
      if (ticker && type != null) txAssetTypeMap[ticker] = type;
    });

    return filtered.map((h) => {
      const ticker = String(h.ticker || '').toUpperCase();
      // 1st priority: user-saved type in localStorage (set when adding the asset)
      try {
        const stored = localStorage.getItem(`assetType:${portfolioId}:${ticker}`);
        if (stored) return { ...h, assetType: stored };
      } catch (_) {}
      // 2nd priority: assetType from transactions
      const txType = txAssetTypeMap[ticker];
      if (txType != null) return { ...h, assetType: txType };
      return h;
    });
  }, [summary, transactions, portfolioId]);

  const tickerColorMap = useMemo(() => {
    const map = new Map();
    let autoIdx = 0;

    // Pass 1: open positions — user-set color or auto-assigned DEFAULT_COLOR
    groupedHoldings.forEach(h => {
      const ticker = String(h.ticker || '').toUpperCase();
      if (!ticker) return;
      if (assetColors[ticker]) {
        map.set(ticker, assetColors[ticker]);
      } else {
        map.set(ticker, DEFAULT_COLORS[autoIdx % DEFAULT_COLORS.length]);
        autoIdx++;
      }
    });

    // Pass 2: user-set colors for closed/non-open positions (e.g. GOOGL after being sold)
    Object.entries(assetColors).forEach(([ticker, color]) => {
      if (ticker && color && !map.has(ticker)) {
        map.set(ticker, color);
      }
    });

    // Pass 3: any remaining buy-transaction tickers without a color (closed, no custom color)
    // — assign the next available DEFAULT_COLOR so markers never fall back to purple.
    const allBuyTickers = [...new Set(
      transactions
        .filter(tx => tx.transactionType === 0)
        .map(tx => String(tx.instrument?.ticker || tx.ticker || '').toUpperCase())
        .filter(Boolean)
    )].sort();

    allBuyTickers.forEach(ticker => {
      if (!map.has(ticker)) {
        map.set(ticker, DEFAULT_COLORS[autoIdx % DEFAULT_COLORS.length]);
        autoIdx++;
      }
    });

    return map;
  }, [groupedHoldings, assetColors, transactions]);

  const closedHoldings = useMemo(() => {
    const all = Array.isArray(summary?.holdings) ? summary.holdings : [];
    return all.filter((h) => {
      const openQty = Number(h?.openQuantity || 0);
      const sellTxs = Number(h?.sellTransactions || 0);
      // Fully closed (openQty < DUST) OR partially sold (has sell transactions)
      return openQty < DUST || sellTxs > 0;
    });
  }, [summary]);

  // Augment closed holdings with sold quantity + average sell price computed
  // from the loaded transactions (API doesn't return these fields reliably).
  const closedHoldingsAugmented = useMemo(() => {
    const sellMap = {};
    transactions
      .filter((tx) => tx.transactionType === 1)
      .forEach((tx) => {
        const ticker = String(tx.instrument?.ticker || tx.ticker || '').toUpperCase();
        if (!ticker) return;
        if (!sellMap[ticker]) sellMap[ticker] = { totalQty: 0, totalValue: 0 };
        const qty = Number(tx.quantity || 0);
        const price = Number(tx.pricePerUnit || 0);
        sellMap[ticker].totalQty += qty;
        sellMap[ticker].totalValue += qty * price;
      });

    return closedHoldings.map((h) => {
      const ticker = String(h.ticker || '').toUpperCase();
      const sell = sellMap[ticker];
      if (!sell || sell.totalQty <= 0) return h;
      return {
        ...h,
        soldQuantity: sell.totalQty,
        averageSellPrice: sell.totalValue / sell.totalQty,
      };
    });
  }, [closedHoldings, transactions]);

  const dividendTransactions = useMemo(() => {
    return transactions
      .filter((tx) => tx.transactionType === 2)
      .sort((a, b) => new Date(b?.occurredAt || 0) - new Date(a?.occurredAt || 0));
  }, [transactions]);

  const otherTransactions = useMemo(() => {
    return transactions.filter(
      (tx) => tx.transactionType !== 0 && tx.transactionType !== 1 && tx.transactionType !== 2
    );
  }, [transactions]);

  // Dividend all-time total (base currency)
  const dividendIncomeAllTime = useMemo(() => {
    return transactions
      .filter((tx) => tx.transactionType === 2)
      .reduce((sum, tx) => {
        const netAmount = Number(tx?.netAmount || 0);
        const fxRateToBase = Number(tx?.fxRateToBase || 0);
        const tradeCurrency = String(tx?.tradeCurrency || '').toUpperCase();
        if (tradeCurrency === reportCurrency) return sum + netAmount;
        if (fxRateToBase > 0) return sum + netAmount * fxRateToBase;
        return sum + netAmount;
      }, 0);
  }, [transactions, reportCurrency]);

  // Per-ticker dividend totals (in native currency for display)
  const dividendByTicker = useMemo(() => {
    const map = {};
    transactions
      .filter((tx) => tx.transactionType === 2)
      .forEach((tx) => {
        const t = String(tx?.instrument?.ticker || tx?.ticker || '').toUpperCase() || 'Unknown';
        if (!map[t]) map[t] = 0;
        map[t] += Number(tx?.netAmount || 0);
      });
    return map;
  }, [transactions]);

  const currentValue = walletOn
    ? Number(summary?.totalValueBase || 0)
    : Number(summary?.holdingsValueBase || 0);
  const holdingsValue = Number(summary?.holdingsValueBase || 0);
  const cashBalance = walletOn ? Number(summary?.cashBalanceBase || 0) : 0;
  const netContributions = Number(summary?.netContributionsBase || 0);
  const realizedPnl = Number(summary?.realizedPnlBase || 0);
  const unrealizedPnl = Number(summary?.unrealizedPnlBase || 0);
  const totalPnl = Number(summary?.totalPnlBase || 0);

  const dividendIncomeTtm = useMemo(() => {
    const now = new Date();
    const ttmStart = new Date(now);
    ttmStart.setFullYear(now.getFullYear() - 1);

    return transactions
      .filter((tx) => tx.transactionType === 2)
      .filter((tx) => {
        const txDate = new Date(tx?.occurredAt || 0);
        return !Number.isNaN(txDate.getTime()) && txDate >= ttmStart;
      })
      .reduce((sum, tx) => {
        const netAmount = Number(tx?.netAmount || 0);
        const fxRateToBase = Number(tx?.fxRateToBase || 0);
        const tradeCurrency = String(tx?.tradeCurrency || '').toUpperCase();

        if (tradeCurrency === reportCurrency) {
          return sum + netAmount;
        }

        if (fxRateToBase > 0) {
          return sum + netAmount * fxRateToBase;
        }

        return sum + netAmount;
      }, 0);
  }, [transactions, reportCurrency]);

  const openCostBasisBase = useMemo(() => {
    return groupedHoldings.reduce(
      (sum, holding) => sum + Number(holding?.costBasisOpenBase || 0),
      0
    );
  }, [groupedHoldings]);

  // Map ticker → { currentPrice, currency } for P/L computation in transaction tables
  const priceByTicker = useMemo(() => {
    const map = {};
    const all = Array.isArray(summary?.holdings) ? summary.holdings : [];
    all.forEach((h) => {
      const t = String(h.ticker || '').toUpperCase();
      if (t) map[t] = { currentPrice: Number(h.currentPrice || 0), currency: h.currency || 'USD' };
    });
    return map;
  }, [summary]);

  const yieldOnCostTtm = useMemo(() => {
    if (openCostBasisBase <= 0) return 0;
    return (dividendIncomeTtm / openCostBasisBase) * 100;
  }, [dividendIncomeTtm, openCostBasisBase]);

  const dividendYieldTtm = useMemo(() => {
    if (holdingsValue <= 0) return 0;
    return (dividendIncomeTtm / holdingsValue) * 100;
  }, [dividendIncomeTtm, holdingsValue]);

  // Annual dividend forecast in base currency.
  // For each holding: annualDividendTotal (native) × (marketValueBase / marketValueNative).
  const annualDividendForecast = useMemo(() => {
    const all = Array.isArray(summary?.holdings) ? summary.holdings : [];
    return all.reduce((sum, h) => {
      const annual = Number(h.annualDividendTotal || 0);
      const native = Number(h.marketValueNative || 0);
      const base   = Number(h.marketValueBase || 0);
      if (annual <= 0 || native <= 0) return sum;
      return sum + annual * (base / native);
    }, 0);
  }, [summary]);

  const monthlyAvgIncomeTtm = useMemo(() => dividendIncomeTtm / 12, [dividendIncomeTtm]);

  // XIRR — annualised internal rate of return based on actual cash flows.
  // BUY = money out (negative), SELL/DIVIDEND = money in (positive).
  // Current portfolio value is added as a positive flow on today's date.
  const portfolioXirr = useMemo(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) return null;
    const flows = [];
    transactions.forEach((tx) => {
      const date = new Date(tx?.occurredAt || 0);
      if (Number.isNaN(date.getTime())) return;
      const net = Number(tx?.netAmount || 0);
      if (net === 0) return;
      const fx  = Number(tx?.fxRateToBase || 0);
      const ccy = String(tx?.tradeCurrency || '').toUpperCase();
      const base = ccy === reportCurrency ? net : (fx > 0 ? net * fx : net);
      const type = Number(tx?.transactionType);
      // 0=BUY (money out), 1=SELL (money in), 2=DIVIDEND (money in)
      let signed;
      if (type === 0)      signed = -Math.abs(base);
      else if (type === 1) signed = +Math.abs(base);
      else if (type === 2) signed = +Math.abs(base);
      else return;
      flows.push({ amount: signed, date });
    });
    // Final flow: today's mark-to-market value (as if we liquidated)
    const liveValue = Number(currentValue || 0);
    if (liveValue > 0) flows.push({ amount: liveValue, date: new Date() });
    return xirr(flows);
  }, [transactions, currentValue, reportCurrency]);

  // Forward 12-month dividend forecast per ticker, in base currency.
  // Per-ticker version of annualDividendForecast for the allocation pie chart.
  const dividendsForwardByTickerBase = useMemo(() => {
    const all = Array.isArray(summary?.holdings) ? summary.holdings : [];
    const out = {};
    all.forEach((h) => {
      const annual = Number(h.annualDividendTotal || 0);
      const native = Number(h.marketValueNative || 0);
      const base   = Number(h.marketValueBase || 0);
      if (annual <= 0 || native <= 0) return;
      const t = String(h.ticker || '').toUpperCase();
      if (!t) return;
      out[t] = (out[t] || 0) + annual * (base / native);
    });
    return out;
  }, [summary]);

  // TTM dividend totals per ticker, in base currency — kept for other widgets if needed
  // eslint-disable-next-line no-unused-vars
  const dividendsTtmByTickerBase = useMemo(() => {
    const now = new Date();
    const ttmStart = new Date(now);
    ttmStart.setFullYear(now.getFullYear() - 1);
    const out = {};
    transactions
      .filter((tx) => tx.transactionType === 2)
      .forEach((tx) => {
        const txDate = new Date(tx?.occurredAt || 0);
        if (Number.isNaN(txDate.getTime()) || txDate < ttmStart) return;
        const t = String(tx?.instrument?.ticker || tx?.ticker || '').toUpperCase();
        if (!t) return;
        const net    = Number(tx?.netAmount || 0);
        const fx     = Number(tx?.fxRateToBase || 0);
        const ccy    = String(tx?.tradeCurrency || '').toUpperCase();
        const base   = ccy === reportCurrency ? net : (fx > 0 ? net * fx : net);
        out[t] = (out[t] || 0) + base;
      });
    return out;
  }, [transactions, reportCurrency]);

  return (
    <div style={uiStyles.page}>
      {error && <div style={uiStyles.errorBox}>{error}</div>}
      {successMessage && <div style={successBoxStyle}>{successMessage}</div>}

      <PortfolioChart
        loading={historyLoading}
        points={displayHistoryPoints}
        reportCurrency={reportCurrency}
        selectedRange={historyRange}
        onChangeRange={setHistoryRange}
        portfolioName={portfolio?.name || 'Portfolio'}
        benchmarkPoints={benchmarkPoints}
        transactions={buySellTransactions}
        benchmarkTicker={effectiveBenchmarkTicker}
        benchmarkLoading={benchmarkLoading}
        benchmarkError={benchmarkError}
        walletOn={walletOn}
        groupedHoldings={groupedHoldings}
        tickerColorMap={tickerColorMap}
        totalLineColor={chartLineColors.total || null}
        investedLineColor={chartLineColors.invested || null}
        benchmarkLineColor={chartLineColors.benchmark || null}
      />

      <section style={heroSectionStyle}>
        <div style={heroSectionHeaderStyle}>
          <div>
            <h3 style={heroSectionTitleStyle}>Visible dashboard metrics</h3>
            <p style={heroSectionSubtitleStyle}>
              Choose which summary cards are shown at the top of the portfolio dashboard.
            </p>
          </div>

          {!isMobile && (
            <div style={heroSectionActionsStyle}>
              <button
                type="button"
                onClick={() => setShowMetricSettings((prev) => !prev)}
                style={metricSettingsButtonStyle}
                aria-label="Customize metrics"
                title="Customize metrics"
              >
                ⚙
              </button>
            </div>
          )}
        </div>

        {showMetricSettings ? (
          <div style={metricSettingsCardInnerStyle}>
            <div style={metricSettingsHeaderStyle}>
              <div />
              <button
                type="button"
                onClick={resetHeroMetrics}
                style={metricResetButtonStyle}
              >
                Reset to default
              </button>
            </div>

            <div style={metricOptionsGridStyle}>
              {getAvailableHeroMetrics(walletOn).map((metric) => {
                const checked = visibleHeroMetricIds.includes(metric.id);

                return (
                  <label key={metric.id} style={metricOptionStyle}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleHeroMetric(metric.id)}
                    />
                    <span>{metric.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}

        <PortfolioHero
          totalValue={currentValue}
          holdingsValue={holdingsValue}
          cashBalance={cashBalance}
          netContributions={walletOn ? netContributions : openCostBasisBase}
          realizedPnl={realizedPnl}
          unrealizedPnl={unrealizedPnl}
          totalPnl={totalPnl}
          dividendIncomeTtm={dividendIncomeTtm}
          dividendIncomeAllTime={dividendIncomeAllTime}
          yieldOnCostTtm={yieldOnCostTtm}
          dividendYieldTtm={dividendYieldTtm}
          annualDividendForecast={annualDividendForecast}
          monthlyAvgIncomeTtm={monthlyAvgIncomeTtm}
          xirr={portfolioXirr}
          walletEnabled={walletOn}
          reportCurrency={reportCurrency}
          visibleMetricIds={
            walletOn
              ? visibleHeroMetricIds
              : visibleHeroMetricIds.filter(
                  (id) => id !== 'cashBalance' && id !== 'holdingsValue'
                )
          }
        />
      </section>

      <HoldingsTable
        loading={loading || summaryLoading}
        groupedHoldings={groupedHoldings}
        portfolioId={portfolioId}
        portfolioSlug={actualPortfolioSlug}
        reportCurrency={reportCurrency}
        isTransactionMode={true}
      />

      {closedHoldingsAugmented.length > 0 && (
        <HoldingsTable
          loading={loading || summaryLoading}
          groupedHoldings={closedHoldingsAugmented}
          portfolioId={portfolioId}
          portfolioSlug={actualPortfolioSlug}
          reportCurrency={reportCurrency}
          isTransactionMode={true}
          isClosed={true}
          title="Closed Positions"
        />
      )}

      <PortfolioAllocationChart
        groupedHoldings={groupedHoldings}
        reportCurrency={reportCurrency}
        loading={loading || summaryLoading}
        tickerColorMap={tickerColorMap}
        onTickerColorChange={handleTickerColorChange}
        currencyColors={currencyColors}
        onCurrencyColorChange={handleCurrencyColorChange}
        assetTypeColors={assetTypeColors}
        onAssetTypeColorChange={handleAssetTypeColorChange}
        portfolioId={portfolioId}
        dividendsByTicker={dividendsForwardByTickerBase}
        showDividends={true}
        onSliceClick={(label, activeTab) => {
          if ((activeTab === 'holdings' || activeTab === 'dividends') && label !== 'Other') {
            navigate(`/portfolio/${actualPortfolioSlug}/${label.toLowerCase()}`);
          }
        }}
      />

      <AddAssetForm
        assetType={assetType}
        setAssetType={setAssetType}
        ticker={ticker}
        setTicker={setTicker}
        quantity={quantity}
        setQuantity={setQuantity}
        buyPrice={buyPrice}
        setBuyPrice={setBuyPrice}
        buyDate={buyDate}
        setBuyDate={setBuyDate}
        handleAddAsset={handleAddAsset}
        displayName={displayName}
        setDisplayName={setDisplayName}
        submitting={submitting}
        tickerSuggestions={tickerSuggestions}
        nameSuggestions={nameSuggestions}
        tickerSuggestionsLoading={tickerSuggestionsLoading}
        nameSuggestionsLoading={nameSuggestionsLoading}
        onTickerChange={handleTickerChange}
        onNameChange={handleNameChange}
        onSelectSuggestion={handleSelectSuggestion}
      />

      <HoldingTransactionsTable
        title="Buy / Sell Transactions"
        loading={transactionsLoading}
        transactions={buySellTransactions}
        onDeleteTransaction={handleDeleteTransaction}
        onUpdateTransaction={handleUpdateTransaction}
        priceByTicker={priceByTicker}
        storageKey={portfolioId ? `txBuySell-${portfolioId}` : null}
      />

      {/* ── Dividends Section — paid tiers only ──────────────────── */}
      {!isFree && (
      <section style={dividendSectionStyle}>
        <div style={dividendHeaderStyle}>
          <h3 style={dividendTitleStyle}>💰 Dividends</h3>
          <BackfillDividendsButton
            portfolioId={portfolioId}
            onDone={() => { fetchSummary(); fetchTransactions(); }}
          />
        </div>

        {/* Summary row */}
        <div style={dividendStatsRowStyle}>
          <div style={dividendStatCardStyle}>
            <span style={dividendStatLabelStyle}>All-time income</span>
            <strong style={dividendStatValueStyle}>{formatCurrency(dividendIncomeAllTime, reportCurrency)}</strong>
          </div>
          <div style={dividendStatCardStyle}>
            <span style={dividendStatLabelStyle}>TTM income</span>
            <strong style={dividendStatValueStyle}>{formatCurrency(dividendIncomeTtm, reportCurrency)}</strong>
          </div>
          <div style={dividendStatCardStyle}>
            <span style={dividendStatLabelStyle}>TTM yield</span>
            <strong style={{ ...dividendStatValueStyle, color: dividendYieldTtm > 0 ? 'var(--success)' : 'var(--text-primary)' }}>{dividendYieldTtm.toFixed(2)}%</strong>
          </div>
          <div style={dividendStatCardStyle}>
            <span style={dividendStatLabelStyle}>Yield on cost</span>
            <strong style={{ ...dividendStatValueStyle, color: yieldOnCostTtm > 0 ? 'var(--success)' : 'var(--text-primary)' }}>{yieldOnCostTtm.toFixed(2)}%</strong>
          </div>
        </div>

        {/* Per-ticker breakdown */}
        {Object.keys(dividendByTicker).length > 0 && (
          <div style={dividendBreakdownStyle}>
            {Object.entries(dividendByTicker)
              .sort(([, a], [, b]) => b - a)
              .map(([t, total]) => {
                const currency = priceByTicker[t]?.currency || reportCurrency;
                return (
                  <div key={t} style={dividendBreakdownItemStyle}>
                    <span style={dividendBreakdownTickerStyle}>{t}</span>
                    <span style={dividendBreakdownAmtStyle}>{formatCurrency(total, currency)}</span>
                  </div>
                );
              })}
          </div>
        )}

        {/* Transaction history (paginated, 10 per page) */}
        {dividendTransactions.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '16px 0 0' }}>
            No dividend transactions yet. Add one via the Wallet section below.
          </p>
        ) : (
          <DividendsPaginatedTable
            rows={dividendTransactions}
            reportCurrency={reportCurrency}
            pageSize={10}
          />
        )}
      </section>
      )}

      {portfolio?.walletEnabled !== false && <section style={walletCardStyle}>
        <div style={walletHeaderStyle}>
          <div>
            <h3 style={walletTitleStyle}>Cash Wallet</h3>
            <p style={walletSubtitleStyle}>
              Cash balances across all currencies in this portfolio.
            </p>
          </div>

          <div style={walletBaseTotalStyle}>
            {formatCurrency(cashBalance, reportCurrency)}
          </div>
        </div>

        {hasNegativeTrackedCash ? (
          <div style={walletWarningStyle}>
            Negative tracked cash is allowed. This usually means some historical funding,
            deposits, or FX transfers have not been recorded yet.
          </div>
        ) : null}

        {visibleCashBalances.length === 0 ? (
          <p style={walletEmptyStyle}>No cash balances yet.</p>
        ) : (
          <div style={walletGridStyle}>
            {visibleCashBalances.map((item) => {
              const isNegative = Boolean(item.isNegative) || Number(item.amount || 0) < 0;
              const amountColor = isNegative ? 'var(--danger)' : 'var(--text-primary)';

              return (
                <div
                  key={item.currency}
                  style={{
                    ...walletItemStyle,
                    ...(isNegative ? walletItemWarningStyle : null),
                  }}
                >
                  <div style={walletItemTopStyle}>
                    <span style={walletCurrencyBadgeStyle}>{item.currency}</span>
                    <span
                      style={{
                        ...walletStatusStyle,
                        color: amountColor,
                      }}
                    >
                      {isNegative ? 'Missing funding history' : 'Tracked'}
                    </span>
                  </div>

                  <div style={{ ...walletAmountStyle, color: amountColor }}>
                    {formatCurrency(item.amount, item.currency)}
                  </div>

                  <div style={walletBaseAmountStyle}>
                    {formatCurrency(item.amountInBase, reportCurrency)}
                  </div>

                  {isNegative ? (
                    <div style={walletItemHintStyle}>
                      This balance is negative because historical deposits or FX transfers may
                      be missing.
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>}

      {portfolio?.walletEnabled !== false && <section style={cashActionsCardStyle}>
        <div style={cashActionsHeaderStyle}>
          <div>
            <h3 style={cashActionsTitleStyle}>Cash Actions</h3>
            <p style={cashActionsSubtitleStyle}>
              Add cash, withdraw cash, record income, charges, and FX conversions.
            </p>
          </div>
        </div>

        <div style={cashActionsGridStyle}>
          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Deposit</h4>

            <form onSubmit={handleDeposit} style={cashFormStyle}>
              <input
                type="number"
                step="any"
                placeholder="Amount"
                value={depositAmount || ''}
                onChange={(e) => setDepositAmount(e.target.value)}
                style={uiStyles.input}
              />

              <CustomSelect
                value={depositCurrency || reportCurrency}
                onChange={setDepositCurrency}
                options={availableCurrencies.map((c) => ({
                  value: c,
                  label: c + (c === reportCurrency ? ' (portfolio base)' : ''),
                }))}
                disabled={submitting}
              />

              <CustomDateInput value={depositDate} onChange={setDepositDate} />

              <button type="submit" style={uiStyles.primaryButton} disabled={submitting}>
                {submitting ? 'Saving...' : 'Deposit'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Withdrawal</h4>

            <div style={helperTextStyle}>
              Tracked cash: {formatNumber(getAvailableCash(withdrawalCurrency || reportCurrency))}{' '}
              {(withdrawalCurrency || reportCurrency).toUpperCase()}
            </div>

            <form onSubmit={handleWithdrawal} style={cashFormStyle}>
              <input
                type="number"
                step="any"
                placeholder="Amount"
                value={withdrawalAmount || ''}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                style={uiStyles.input}
              />

              <CustomSelect
                value={withdrawalCurrency || reportCurrency}
                onChange={setWithdrawalCurrency}
                options={availableCurrencies.map((c) => ({ value: c, label: c }))}
                disabled={submitting}
              />

              <CustomDateInput value={withdrawalDate} onChange={setWithdrawalDate} />

              <button type="submit" style={dangerButtonStyle} disabled={submitting}>
                {submitting ? 'Saving...' : 'Withdraw'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Dividend</h4>

            <form onSubmit={handleDividend} style={cashFormStyle}>
              <CustomSelect
                value={dividendTicker || ''}
                onChange={setDividendTicker}
                options={
                  holdingOptions.length === 0
                    ? [{ value: '', label: 'No holdings available' }]
                    : holdingOptions.map((item) => ({
                        value: item.ticker,
                        label: `${item.ticker} — ${item.displayName}`,
                      }))
                }
                disabled={submitting || holdingOptions.length === 0}
              />

              <input
                type="number"
                step="any"
                placeholder="Dividend amount"
                value={dividendAmount || ''}
                onChange={(e) => setDividendAmount(e.target.value)}
                style={uiStyles.input}
              />

              <input
                type="text"
                value={dividendCurrency || ''}
                readOnly
                style={{ ...uiStyles.input, background: 'var(--surface)' }}
              />

              <CustomDateInput value={dividendDate} onChange={setDividendDate} />

              <button
                type="submit"
                style={uiStyles.primaryButton}
                disabled={submitting || holdingOptions.length === 0}
              >
                {submitting ? 'Saving...' : 'Add dividend'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Interest</h4>

            <form onSubmit={handleInterest} style={cashFormStyle}>
              <input
                type="number"
                step="any"
                placeholder="Interest amount"
                value={interestAmount || ''}
                onChange={(e) => setInterestAmount(e.target.value)}
                style={uiStyles.input}
              />

              <CustomSelect
                value={interestCurrency || reportCurrency}
                onChange={setInterestCurrency}
                options={availableCurrencies.map((c) => ({ value: c, label: c }))}
                disabled={submitting}
              />

              <CustomDateInput value={interestDate} onChange={setInterestDate} />

              <button type="submit" style={uiStyles.primaryButton} disabled={submitting}>
                {submitting ? 'Saving...' : 'Add interest'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Fee</h4>

            <div style={helperTextStyle}>
              Tracked cash: {formatNumber(getAvailableCash(feeCurrency || reportCurrency))}{' '}
              {(feeCurrency || reportCurrency).toUpperCase()}
            </div>

            <form onSubmit={handleFee} style={cashFormStyle}>
              <input
                type="number"
                step="any"
                placeholder="Fee amount"
                value={feeAmountValue || ''}
                onChange={(e) => setFeeAmountValue(e.target.value)}
                style={uiStyles.input}
              />

              <CustomSelect
                value={feeCurrency || reportCurrency}
                onChange={setFeeCurrency}
                options={availableCurrencies.map((c) => ({ value: c, label: c }))}
                disabled={submitting}
              />

              <CustomDateInput value={feeDate} onChange={setFeeDate} />

              <button type="submit" style={dangerButtonStyle} disabled={submitting}>
                {submitting ? 'Saving...' : 'Add fee'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Tax</h4>

            <div style={helperTextStyle}>
              Tracked cash: {formatNumber(getAvailableCash(taxCurrency || reportCurrency))}{' '}
              {(taxCurrency || reportCurrency).toUpperCase()}
            </div>

            <form onSubmit={handleTax} style={cashFormStyle}>
              <input
                type="number"
                step="any"
                placeholder="Tax amount"
                value={taxAmount || ''}
                onChange={(e) => setTaxAmount(e.target.value)}
                style={uiStyles.input}
              />

              <CustomSelect
                value={taxCurrency || reportCurrency}
                onChange={setTaxCurrency}
                options={availableCurrencies.map((c) => ({ value: c, label: c }))}
                disabled={submitting}
              />

              <CustomDateInput value={taxDate} onChange={setTaxDate} />

              <button type="submit" style={dangerButtonStyle} disabled={submitting}>
                {submitting ? 'Saving...' : 'Add tax'}
              </button>
            </form>
          </div>

          <div style={cashActionPanelStyle}>
            <h4 style={cashPanelTitleStyle}>Convert Cash</h4>

            <div style={helperTextStyle}>
              Tracked source cash:{' '}
              {formatNumber(getAvailableCash(conversionFromCurrency || reportCurrency))}{' '}
              {(conversionFromCurrency || reportCurrency).toUpperCase()}
            </div>

            <form onSubmit={handleFxConversion} style={cashFormStyle}>
              <CustomSelect
                value={conversionFromCurrency || ''}
                onChange={setConversionFromCurrency}
                options={
                  convertibleFromCurrencies.length === 0
                    ? [{ value: '', label: 'No source currency' }]
                    : convertibleFromCurrencies.map((c) => ({ value: c, label: `From: ${c}` }))
                }
                disabled={submitting || convertibleFromCurrencies.length === 0}
              />

              <input
                type="number"
                step="any"
                placeholder="Source amount"
                value={conversionFromAmount || ''}
                onChange={(e) => {
                  setConversionFromAmount(e.target.value);
                  setLastEditedConversionField('from');
                }}
                style={uiStyles.input}
              />

              <CustomSelect
                value={conversionToCurrency || ''}
                onChange={setConversionToCurrency}
                options={convertibleToCurrencies.map((c) => ({ value: c, label: `To: ${c}` }))}
                disabled={submitting}
              />

              <input
                type="number"
                step="any"
                placeholder="FX rate"
                value={conversionRate || ''}
                onChange={(e) => {
                  setConversionRate(e.target.value);
                  setLastEditedConversionField('rate');
                }}
                style={uiStyles.input}
              />

              <input
                type="number"
                step="any"
                placeholder="Target amount received"
                value={conversionToAmount || ''}
                onChange={(e) => {
                  setConversionToAmount(e.target.value);
                  setLastEditedConversionField('to');
                }}
                style={uiStyles.input}
              />

              <div style={fxInfoStyle}>
                {conversionRateLoading
                  ? 'Loading FX rate...'
                  : conversionRate
                  ? `Current rate: 1 ${conversionFromCurrency || '-'} = ${conversionRate} ${conversionToCurrency || '-'}`
                  : 'Select currencies and date to load the FX rate, or enter any two values manually.'}
              </div>

              <CustomDateInput value={conversionDate} onChange={setConversionDate} />

              <button
                type="submit"
                style={uiStyles.primaryButton}
                disabled={
                  submitting ||
                  !conversionFromCurrency ||
                  !conversionToCurrency ||
                  !conversionFromAmount ||
                  !conversionRate ||
                  !conversionToAmount
                }
              >
                {submitting ? 'Converting...' : 'Convert'}
              </button>
            </form>
          </div>
        </div>
      </section>}

      {!isFree && (
        <HoldingTransactionsTable
          title="Other Transactions"
          loading={transactionsLoading}
          transactions={otherTransactions}
          onDeleteTransaction={handleDeleteTransaction}
          onUpdateTransaction={handleUpdateTransaction}
        />
      )}
    </div>
  );
}

function mapAssetType(assetType) {
  switch (assetType) {
    case 'Stock':
      return 0;
    case 'ETF':
      return 1;
    case 'Crypto':
      return 2;
    case 'Bond':
      return 3;
    case 'Commodity':
      return 4;
    case 'Fund':
      return 5;
    default:
      return 99;
  }
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 6,
  }).format(Number(value || 0));
}

const heroSectionStyle = {
  marginBottom: '24px',
};

const heroSectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '14px',
};

const heroSectionTitleStyle = {
  marginTop: 0,
  marginBottom: '6px',
  color: 'var(--text-primary)',
};

const heroSectionSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const heroSectionActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const metricSettingsButtonStyle = {
  width: '46px',
  height: '46px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  fontWeight: 700,
  fontSize: '20px',
  cursor: 'pointer',
  background: 'var(--surface-strong)',
  color: 'var(--text-primary)',
  padding: 0,
};

const metricSettingsCardInnerStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '20px 24px',
  marginBottom: '16px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const metricSettingsHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const metricOptionsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: '12px',
};

const metricOptionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
  fontWeight: 600,
};

const metricResetButtonStyle = {
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '10px 14px',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontWeight: 700,
};

const successBoxStyle = {
  marginBottom: '16px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'rgba(34, 197, 94, 0.12)',
  color: '#86efac',
  border: '1px solid rgba(34, 197, 94, 0.35)',
  fontWeight: 600,
};

const helperTextStyle = {
  marginBottom: '10px',
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
};

const walletCardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const walletHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '20px',
};

const walletTitleStyle = {
  marginTop: 0,
  marginBottom: '6px',
  color: 'var(--text-primary)',
};

const walletSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const walletBaseTotalStyle = {
  color: 'var(--text-primary)',
  fontSize: '24px',
  fontWeight: 800,
};

const walletWarningStyle = {
  marginBottom: '16px',
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'rgba(245, 158, 11, 0.12)',
  color: '#fcd34d',
  border: '1px solid rgba(245, 158, 11, 0.35)',
  fontWeight: 600,
  lineHeight: 1.5,
};

const walletEmptyStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
};

const walletGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: '16px',
};

const walletItemStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  padding: '18px',
};

const walletItemWarningStyle = {
  border: '1px solid rgba(245, 158, 11, 0.45)',
  boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.12) inset',
};

const walletItemTopStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '12px',
};

const walletCurrencyBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '56px',
  padding: '6px 10px',
  borderRadius: '999px',
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
  fontSize: '12px',
  fontWeight: 800,
};

const walletStatusStyle = {
  fontSize: '12px',
  fontWeight: 700,
};

const walletAmountStyle = {
  fontSize: '24px',
  fontWeight: 800,
  marginBottom: '8px',
};

const walletBaseAmountStyle = {
  color: 'var(--text-secondary)',
  fontSize: '14px',
  fontWeight: 600,
};

const walletItemHintStyle = {
  marginTop: '12px',
  fontSize: '12px',
  lineHeight: 1.45,
  color: '#fcd34d',
};

const cashActionsCardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const cashActionsHeaderStyle = {
  marginBottom: '20px',
};

const cashActionsTitleStyle = {
  marginTop: 0,
  marginBottom: '6px',
  color: 'var(--text-primary)',
};

const cashActionsSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const cashActionsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
  gap: '20px',
};

const cashActionPanelStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  padding: '18px',
};

const cashPanelTitleStyle = {
  marginTop: 0,
  marginBottom: '14px',
  color: 'var(--text-primary)',
};

const cashFormStyle = {
  display: 'grid',
  gap: '12px',
};

const fxInfoStyle = {
  marginTop: '-4px',
  fontSize: '13px',
  color: 'var(--text-secondary)',
  lineHeight: 1.4,
};

const dangerButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 700,
  cursor: 'pointer',
  background: '#dc2626',
  color: '#ffffff',
};

// ── Dividend section styles ───────────────────────────────────────────────────
const dividendSectionStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '20px 24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  width: '100%',
  boxSizing: 'border-box',
};
const dividendHeaderStyle = { marginBottom: '16px' };
const dividendTitleStyle = { margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' };
const dividendStatsRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '12px',
  marginBottom: '16px',
};
const dividendStatCardStyle = {
  background: 'var(--surface)',
  borderRadius: '12px',
  padding: '12px 16px',
  border: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};
const dividendStatLabelStyle = { fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const dividendStatValueStyle = { fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)' };
const dividendBreakdownStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '4px',
};
const dividendBreakdownItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(34,197,94,0.10)',
  border: '1px solid rgba(34,197,94,0.20)',
  borderRadius: '8px',
  padding: '4px 10px',
};
const dividendBreakdownTickerStyle = { fontSize: '12px', fontWeight: 800, color: 'var(--text-primary)' };
const dividendBreakdownAmtStyle = { fontSize: '12px', fontWeight: 700, color: '#22c55e' };
export default PortfolioDashboard;